import React, { Component } from 'react';
import { connect } from 'react-redux';
import UserDetailsComponent from './UserDetails'
import { getUserDetails, updateUserDetails } from '../../services/java/java-services';
import { sendEmail, updateUserDetailsCognito } from '../../services/aws/aws-services';
import { loggerEventName, loggerEventOutcome, loggerEventTypes, userMessages } from '../../helpers/messages';
import { validateAlphanumeric, validateEmail, validateUserFields } from '../../shared/ValidateUser';
import { charset, getUserEmailChangedTemplate, sourceEmail, subjectLines } from '../../helpers/Email';
import { logger } from '../../services/logger/logger-service';

let startDate;
class UserDetailsContainer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            editMode: false,
            userName: props.userName ? props.userName : '',
            user: '',
            userError: null,
            showSuccessMsg: '',
            showEmailSuccessMsg: '',
            showErrorMsg: '',
            editObj: {
                firstName: '',
                lastName: '',
                email: ''
            },
            errors: {
                firstName: '',
                lastName: '',
                email: ''
            }
        }
    }

    componentDidMount() {
        startDate = new Date();
        window.addEventListener('beforeunload', this.timeSpentLogger);
        this.getUserDetails(this.props.userName);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.timeSpentLogger);
        this.timeSpentLogger();
    }
    
    /**
     * @description function to handle application logs for time spent on the page
     * @param {*}
     * @memberof UserDetailsContainer
     */
     timeSpentLogger(key) {
        // calculate the time since we loaded this page
        const timeSinceLoad = (new Date().getTime() - startDate.getTime()) / 1000;
        const loggerObj = {
            "EventOutcome": loggerEventOutcome.success,
            "EventType": loggerEventTypes.read,
            "EventName": loggerEventName.userDetails,
            "Content": {
                "TimeSpent": timeSinceLoad
            }
        }
        logger(loggerObj);
    }

    /**
     * @description Function to get the user details
     * @param userName Unique username of the user to be fetched
     * @memberof UserDetailsContainer
     */
    getUserDetails = async (username, rippleProp = false) => {
        this.setState({ loading: true })
        getUserDetails(username, (err, result) => {
            /* istanbul ignore next  */
            if (err) {
                this.setState({ loading: false, userError: err })
            } else {
                const user = result.data && result.data.user && result.data.user.length ? result.data.user[0] : '';
                this.setState({
                    loading: false,
                    user: user,
                    userError: null,
                    ...this.getEditObject(user)
                });
                if (rippleProp) {
                    setTimeout(() => {
                        this.props.userUpdated(true);
                    }, 0);
                }
            }
        })
    }

    /**
     * @description Function to handle state when edit is cancelled
     * @param user User Details
     * @memberof UserDetailsContainer
     */
    getEditObject(user) {
        const editObj = {
            firstName: user && user.firstName ? user.firstName : '',
            lastName: user && user.lastName ? user.lastName : '',
            email: user && user.email ? user.email : '',
            group: user && user.group ? user.group : '',
        }
        return {
            editObj: editObj,
            errors: {
                firstName: '',
                lastName: '',
                email: ''
            }
        }
    }

    /**
     * @description Function to enable/disable edit mode
     * @memberof UserDetailsContainer
     */
    toggleEditMode = () => {
        this.setState(prevState => {
            return {
                ...this.getEditObject(prevState.user),
                editMode: !prevState.editMode
            };
        });
    }

    /**
     * @description Function to handle input changes
     * @param  value Value of the input
     * @param  field Input field name
     * @memberof UserDetailsContainer
     */
    handleInputChange = (field, value) => {
        var errorMessage = !value ? userMessages[field] : '';
        if ((field === 'firstName' || field === 'lastName') && value) {
            const surgeonNameCheck = validateAlphanumeric(value);
            errorMessage = surgeonNameCheck.error;
        }
        if (field === 'email' && value) {
            const emailCheck = validateEmail(value);
            errorMessage = emailCheck.error;
        }
        this.setState(prevState => {
            return {
                [field]: value,
                editObj: {
                    ...prevState.editObj,
                    [field]: value
                },
                errors: {
                    ...prevState.errors,
                    [field]: errorMessage
                }
            }
        });
    }

    /** 
     * @description Function submit form by hitting enter key
     *  @param e Keypress event
     *  @memberof UserDetailsContainer
     */
    onEnterPress = (e) => {
        if (e.which === 13) {
            e.preventDefault();
            this.saveUser();
        }
    }

    /**
     * @description Function to validate fields at the time of submit
     * @returns Boolean value if there are any errors
     * @memberof UserDetailsContainer
     */
    validateFields = () => {
        const { editObj } = this.state;
        let { errorFlag, errors } = validateUserFields({ editObj: editObj, errors: this.state.errors });
        if (!errorFlag && editObj.firstName) {
            let firstName = validateAlphanumeric(editObj.firstName);
            errorFlag = firstName.errorFlag;
            errors['firstName'] = firstName.error;
        }
        if (!errorFlag && editObj.lastName) {
            let lastName = validateAlphanumeric(editObj.lastName);
            errorFlag = lastName.errorFlag;
            errors['lastName'] = lastName.error;
        }
        if (!errorFlag && editObj.email) {
            let email = validateEmail(editObj.email);
            errorFlag = email.errorFlag;
            errors['email'] = email.error;
        }

        if (errorFlag) {
            this.setState(prevState => {
                return {
                    errors: {
                        ...prevState.errors,
                        ...errors
                    }
                }
            })
        }

        return errorFlag;
    }

    /**
     * @description function to validate and save user details
     * @param {*}
     * @memberof UserDetailsContainer
     */
    saveUser = () => {
        this.setState({ showSuccessMsg: '', showErrorMsg: '' });
        const formHasErrors = this.validateFields();

        if (!formHasErrors) {
            const { editObj, user } = this.state;
            this.setState({ loading: true });
            const isUpdateCognito = editObj.firstName !== user.firstName || editObj.lastName !== user.lastName || editObj.email !== user.email;
            let updateAttributes = {};
            if (editObj.firstName !== user.firstName) {
                updateAttributes['firstname'] = editObj.firstName;
            }

            if (editObj.lastName !== user.lastName) {
                updateAttributes['lastname'] = editObj.lastName;
            }

            if (editObj.email !== user.email) {
                updateAttributes['email'] = editObj.email;
            }

            if (isUpdateCognito) {
                this.updateCognito(updateAttributes);
            } else {
                this.updateUserAPI(updateAttributes);
            }
        }
    }

    /**
     * @description function to update cognito userpool
     * @param {*}
     * @memberof UserDetailsContainer
     */
    updateCognito = (updateAttributes) => {
        this.setState({ showSuccessMsg: '', showErrorMsg: '' });
        const { userName, user } = this.state;
        var isSendEmail = false;
        let cognitoUserAttributes = [];

        if (updateAttributes.firstname) {
            cognitoUserAttributes.push({ Name: 'given_name', Value: updateAttributes.firstname });
        }

        if (updateAttributes.lastname) {
            cognitoUserAttributes.push({ Name: 'family_name', Value: updateAttributes.lastname });
        }

        if (updateAttributes.email) {
            isSendEmail = true;
            cognitoUserAttributes.push({ Name: 'email', Value: updateAttributes.email });
            cognitoUserAttributes.push({ Name: 'email_verified', Value: "true" });
        }

        updateUserDetailsCognito(userName, cognitoUserAttributes)
            .then(res => {
                if (res) {
                    if (isSendEmail) {
                        const toEmailIDs = [
                            user.email,
                            updateAttributes.email
                        ]
                        this.sendEmailToUser(toEmailIDs, updateAttributes);
                    }
                    this.updateUserAPI(updateAttributes);
                }
            })
            .catch(err => {
                this.setState({ loading: false, showErrorMsg: err && err.message ? err.message : userMessages.saveError });
            });
    }

    /**
     * @description function to notify user of an email update
     * @param {*}
     * @memberof UserDetailsContainer
     */
    sendEmailToUser = (toEmailIDs, updatedDetails) => {
        const user = {
            firstName: updatedDetails.firstname ? updatedDetails.firstname : this.state.user.firstName,
            lastName: updatedDetails.lastname ? updatedDetails.lastname : this.state.user.lastName,
            email: updatedDetails.email
        }
        const params = {
            Destination: {
                ToAddresses: toEmailIDs
            },
            Message: {
                Body: {
                    Html: {
                        Charset: charset,
                        Data: getUserEmailChangedTemplate(user)
                    }
                },
                Subject: {
                    Charset: charset,
                    Data: subjectLines.emailChange
                }
            },
            Source: sourceEmail
        };
        sendEmail(params, (res) => {
            this.setState({ showEmailSuccessMsg: userMessages.emailSent })
        })
    }

    /**
     * @description function to update user in db
     * @param {*}
     * @memberof UserDetailsContainer
     */
    updateUserAPI = (updateAttributes) => {
        this.setState({ showSuccessMsg: '', showErrorMsg: '' });
        const { userName } = this.state;
        const params = {
            ...updateAttributes,
            usertype: 'all'
        }

        updateUserDetails(userName, params, (err, res) => {
            if (err) {
                const errMsg = err && err.message ? err.message : userMessages.saveError;
                this.setState({ loading: false, showErrorMsg: errMsg });
                this.applicationLogger({ outcome: loggerEventOutcome.failure, message: `${errMsg} (For ${userName})` });
                this.props.userUpdated(false);
            } else {
                this.setState({ showSuccessMsg: userMessages.saveUserMsg, showErrorMsg: '', loading: false, editMode: false });
                this.getUserDetails(userName, true);
            }
        });
    }

    /**
     * @description function to log user update event
     * @memberof UserDetailsContainer
     */
    /* istanbul ignore next  */
    applicationLogger = (params) => {
        const loggerObj = {
            "EventOutcome": params.outcome,
            "EventType": loggerEventTypes.update,
            "EventName": loggerEventName.userUpdate,
            "Content": {
                "Data": params.message
            }
        }
        logger(loggerObj);
    }

    render() {
        return (
            <UserDetailsComponent
                user={this.state.user}
                loading={this.state.loading}
                userError={this.state.userError}
                editMode={this.state.editMode}
                toggleEditMode={this.toggleEditMode}
                editObj={this.state.editObj}
                handleInputChange={this.handleInputChange}
                onEnterPress={this.onEnterPress}
                showSuccessMsg={this.state.showSuccessMsg}
                showEmailSuccessMsg={this.state.showEmailSuccessMsg}
                showErrorMsg={this.state.showErrorMsg}
                formErrors={this.state.errors}
                saveUser={this.saveUser}
                handleCancel={this.props.handleCancel}
            />
        );
    }
}

function mapStateToProps(state) {
    return {
        state,
    };
}

export default connect(mapStateToProps)(UserDetailsContainer);